import {
  EMPTY_INPUT,
  PLAYER_H,
  PLAYER_W,
  TICK_RATE,
  WORLD_H,
  WORLD_W,
  type CrateState,
  type GameEvent,
  type InputState,
  type LevelDef,
  type Platform,
  type PlayerInfo,
  type PlayerState,
  type Rect,
  type WorldState,
} from './types';

export const PHYSICS = Object.freeze({
  speed: 220,
  jump: 480,
  gravity: 1250,
  maxFall: 760,
  coyote: 0.1,
  buffer: 0.12,
});

const STEP = 1 / TICK_RATE;
const EPS = 1e-7;
const CONTACT = 1e-4;
const EVENT_LIMIT = 64;
const TAU = Math.PI * 2;

type BodyState = PlayerState | CrateState;
interface Body {
  key: string;
  state: BodyState;
  player: PlayerState | null;
  start: Rect;
  wasGrounded: boolean;
  support: Support | null;
  ownX: number;
  ownY: number;
  targetY: number;
  jumped: boolean;
  /** An idle courier standing on a pressure plate cannot be shoved off it by friends. */
  anchored: boolean;
}
interface Solid {
  key: string;
  id: string;
  rect: Rect;
  previous: Rect;
}
interface Support {
  key: string;
  id: string;
  rect: Rect;
  body?: Body;
  solid?: Solid;
}
interface AxisNode {
  body: Body;
  min: number;
  max: number;
  size: number;
  after: AxisNode[];
}

const compareId = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const right = (r: Rect) => r.x + r.w;
const bottom = (r: Rect) => r.y + r.h;
const overlapX = (a: Rect, b: Rect) => Math.min(right(a), right(b)) - Math.max(a.x, b.x);
const overlapY = (a: Rect, b: Rect) => Math.min(bottom(a), bottom(b)) - Math.max(a.y, b.y);
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const rectOf = (r: Rect): Rect => ({ x: r.x, y: r.y, w: r.w, h: r.h });

/** Edge contact is not penetration. The tolerance only absorbs floating-point roundoff. */
export function overlaps(a: Rect, b: Rect): boolean {
  return a.w > 0 && a.h > 0 && b.w > 0 && b.h > 0 && overlapX(a, b) > EPS && overlapY(a, b) > EPS;
}

function switchesOn(ids: string[], world: WorldState): boolean {
  return ids.every(id => world.switches.some(s => s.id === id && s.active));
}

export function isGateOpen(gate: LevelDef['gates'][number], world: WorldState): boolean {
  return switchesOn(gate.switchIds, world) && (!gate.requireKey || world.collectedKeys.length > 0);
}

export function isHazardActive(hazard: LevelDef['hazards'][number], world: WorldState): boolean {
  if (hazard.disabledBy?.length && switchesOn(hazard.disabledBy, world)) return false;
  if (hazard.cycleAfterKey && world.collectedKeys.length === 0) return true;
  if (!hazard.cycle) return true;
  const { on, period, phase } = hazard.cycle;
  if (on <= 0) return false;
  if (period <= 0 || on >= period) return true;
  const time = ((world.elapsed + phase) % period + period) % period;
  return time < on - EPS;
}

export function getPlatforms(level: LevelDef, world: WorldState): Platform[] {
  return level.platforms.filter(p => !p.switchIds || switchesOn(p.switchIds, world)).map(p => {
    const platform = { ...p };
    if (p.motion && p.motion.period > 0) {
      // distance is the full signed travel from the authored position; phase is seconds.
      const { axis, distance, period, phase = 0 } = p.motion;
      platform[axis] += distance * (1 - Math.cos(TAU * (world.elapsed + phase) / period)) / 2;
    }
    return platform;
  });
}

function emit(world: WorldState, type: GameEvent['type'], detail: Omit<Partial<GameEvent>, 'id' | 'tick' | 'type'> = {}): void {
  world.events.push({ id: world.nextEventId++, tick: world.tick, type, ...detail });
  if (world.events.length > EVENT_LIMIT) world.events.splice(0, world.events.length - EVENT_LIMIT);
}

function bodiesFor(world: WorldState): Body[] {
  const make = (state: BodyState, player: PlayerState | null): Body => ({
    key: `${player ? 'player' : 'crate'}:${state.id}`,
    state,
    player,
    start: rectOf(state),
    wasGrounded: state.grounded,
    support: null,
    ownX: 0,
    ownY: 0,
    targetY: state.y,
    jumped: false,
    anchored: false,
  });
  return [
    ...world.players.filter(p => p.connected && !p.entered).map(p => make(p, p)),
    ...world.crates.map(c => make(c, null)),
  ].sort((a, b) => compareId(a.key, b.key));
}

function solidsFor(level: LevelDef, world: WorldState, previous: Solid[] = []): Solid[] {
  const old = new Map(previous.map(s => [s.key, s.rect]));
  const platforms = getPlatforms(level, world).map(p => ({ key: `platform:${p.id}`, id: p.id, rect: rectOf(p) }));
  const gates = level.gates.filter(g => !isGateOpen(g, world)).map(g => ({ key: `gate:${g.id}`, id: g.id, rect: rectOf(g) }));
  return [...platforms, ...gates].map(s => ({ ...s, previous: old.get(s.key) ?? s.rect }))
    .sort((a, b) => compareId(a.key, b.key));
}

function supportsFor(body: Body, bodies: Body[], solids: Solid[]): Support[] {
  const feet = bottom(body.state);
  const contacts: Support[] = [];
  for (const solid of solids) {
    if (Math.abs(feet - solid.rect.y) <= CONTACT && overlapX(body.state, solid.rect) > EPS) {
      contacts.push({ key: solid.key, id: solid.id, rect: solid.rect, solid });
    }
  }
  for (const other of bodies) {
    if (body !== other && other.state.y > body.state.y &&
      Math.abs(feet - other.state.y) <= CONTACT && overlapX(body.state, other.state) > EPS) {
      contacts.push({ key: other.key, id: other.state.id, rect: other.state, body: other });
    }
  }
  return contacts.sort((a, b) => {
    const preferred = body.player?.supportId;
    return Number(b.id === preferred) - Number(a.id === preferred) ||
      overlapX(body.state, b.rect) - overlapX(body.state, a.rect) || compareId(a.key, b.key);
  });
}

function refreshGrounding(bodies: Body[], solids: Solid[]): void {
  for (const body of bodies) {
    const support = supportsFor(body, bodies, solids)[0] ?? null;
    body.state.grounded = !!support && body.state.vy >= -EPS;
    body.support = body.state.grounded ? support : null;
    if (body.player) {
      body.player.supportId = body.support?.id ?? null;
      if (body.state.grounded) body.player.coyote = PHYSICS.coyote;
    }
    if (body.state.grounded) body.state.vy = 0;
  }
}

/** Used only for spawning or newly materialized geometry, never as the ordinary movement solver. */
function repairEmbeddings(bodies: Body[], solids: Solid[], maxDistance = Infinity): boolean {
  for (const body of [...bodies].sort((a, b) => b.state.y - a.state.y || compareId(a.key, b.key))) {
    const state = body.state;
    const blockers: Rect[] = [...solids.map(s => s.rect), ...bodies.filter(b => b !== body).map(b => b.state)];
    if (state.x >= 0 && right(state) <= WORLD_W && state.y >= 0 && !blockers.some(r => overlaps(state, r))) continue;
    const xs = new Set([clamp(state.x, 0, WORLD_W - state.w), 0, WORLD_W - state.w]);
    const ys = new Set([Math.max(0, state.y), 0]);
    for (const r of blockers) {
      xs.add(r.x - state.w);
      xs.add(right(r));
      ys.add(r.y - state.h);
      ys.add(bottom(r));
    }
    const candidates: { x: number; y: number; distance: number }[] = [];
    for (const x of xs) for (const y of ys) {
      const distance = Math.abs(x - state.x) + Math.abs(y - state.y);
      if (x < -EPS || x + state.w > WORLD_W + EPS || y < 0 || y > WORLD_H || distance > maxDistance) continue;
      candidates.push({ x, y, distance });
    }
    candidates.sort((a, b) => a.distance - b.distance || a.y - b.y || a.x - b.x);
    const spot = candidates.find(p => !blockers.some(r => overlaps({ ...state, x: p.x, y: p.y }, r)));
    if (!spot) return false;
    state.x = spot.x;
    state.y = spot.y;
    state.vx = 0;
    state.vy = 0;
  }
  return true;
}

function spawnPlayer(level: LevelDef, info: PlayerInfo, index: number): PlayerState {
  const spawn = level.spawn[index % Math.max(1, level.spawn.length)] ?? { x: 64, y: 515 };
  const extra = level.spawn.length ? Math.floor(index / level.spawn.length) : index;
  return {
    ...info,
    x: clamp(spawn.x + extra * (PLAYER_W + 8), 0, WORLD_W - PLAYER_W),
    y: spawn.y,
    w: PLAYER_W,
    h: PLAYER_H,
    vx: 0,
    vy: 0,
    grounded: false,
    facing: 1,
    entered: false,
    lastInput: 0,
    supportId: null,
    coyote: 0,
    jumpBuffer: 0,
    jumpHeld: false,
  };
}

/** Allocate a group's shared helper budget once, rather than capping every plate independently. */
function assignSwitchRequirements(world: WorldState, level: LevelDef): void {
  const connected = world.players.filter(p => p.connected).length;
  const requirements = new Map<string, number>();
  const groups = new Map<string, LevelDef['switches']>();
  for (const def of level.switches) {
    if (def.byConnected && def.byConnected.length) {
      const table = def.byConnected;
      requirements.set(def.id, Math.max(0, Math.floor(table[Math.min(connected, table.length - 1)] ?? def.required)));
    } else if (def.teamWeight) {
      const members = groups.get(def.teamWeight.group) ?? [];
      members.push(def);
      groups.set(def.teamWeight.group, members);
      requirements.set(def.id, 0);
    } else {
      // Retain the existing per-plate rule, including weight reserved for crates.
      requirements.set(def.id, Math.max(1, Math.min(def.required,
        Math.max(1, connected) + Math.max(0, def.required - world.partySize))));
    }
  }
  for (const members of groups.values()) {
    members.sort((a, b) => a.teamWeight!.priority - b.teamWeight!.priority || compareId(a.id, b.id));
    const capacities = members.map(def => Math.max(0, Math.floor(def.required)));
    // Members normally share one reserve. Honor the largest if authored values differ.
    const reserve = Math.max(0, ...members.map(def => Math.ceil(def.teamWeight!.reserve)));
    let remaining = Math.min(capacities.reduce((sum, capacity) => sum + capacity, 0), Math.max(0, connected - reserve));
    // Whole-helper rounds give earlier priorities the remainder. Saturated plates
    // are skipped, so unequal authored caps neither waste nor duplicate the budget.
    while (remaining > 0) {
      for (const [i, def] of members.entries()) {
        if (remaining === 0) break;
        const required = requirements.get(def.id)!;
        if (required < capacities[i]) {
          requirements.set(def.id, required + 1);
          remaining--;
        }
      }
    }
  }
  for (const state of world.switches) state.required = requirements.get(state.id);
}

export function createWorld(level: LevelDef, players: PlayerInfo[], epoch = 1): WorldState {
  const world: WorldState = {
    tick: 0,
    epoch,
    levelIndex: level.id,
    partySize: players.filter(p => p.connected).length,
    elapsed: 0,
    deaths: 0,
    complete: false,
    completedAt: null,
    players: players.map((p, i) => spawnPlayer(level, p, i)),
    crates: level.crates.map(c => ({ ...c, weight: c.weight ?? 2, vx: 0, vy: 0, grounded: false })),
    switches: level.switches.map(s => ({ id: s.id, active: false, count: 0, latched: false, graceLeft: 0 })),
    collectedKeys: [],
    events: [],
    nextEventId: 1,
  };
  assignSwitchRequirements(world, level);
  for (const [i, state] of world.switches.entries()) {
    if (state.required === 0) {
      state.active = true;
      state.graceLeft = level.switches[i].mode === 'hold' ? Math.max(0, level.switches[i].grace ?? 0) : 0;
    }
  }
  const bodies = bodiesFor(world);
  const solids = solidsFor(level, world);
  if (!repairEmbeddings(bodies, solids)) throw new Error(`Level ${level.id} has no unoccupied spawn space.`);
  refreshGrounding(bodies, solids);
  emit(world, 'start');
  return world;
}

export function resetWorld(world: WorldState, level: LevelDef, requestedBy?: string): void {
  const fresh = createWorld(level, world.players, world.epoch + 1);
  const acknowledgements = new Map(world.players.map(p => [p.id, p.lastInput]));
  fresh.players.forEach(p => { p.lastInput = acknowledgements.get(p.id) ?? 0; });
  Object.assign(world, fresh, {
    tick: world.tick,
    partySize: Math.max(world.partySize, fresh.partySize),
    deaths: world.deaths + 1,
    events: world.events,
    nextEventId: world.nextEventId,
  });
  // createWorld sees only the current roster; regular plates still use the retained design baseline.
  assignSwitchRequirements(world, level);
  emit(world, 'reset', requestedBy ? { playerId: requestedBy } : {});
}

function stopPlayer(player: PlayerState): void {
  player.vx = 0;
  player.vy = 0;
  player.grounded = false;
  player.supportId = null;
  player.coyote = 0;
  player.jumpBuffer = 0;
  player.jumpHeld = false;
}

function completeIfReady(world: WorldState): void {
  const connected = world.players.filter(p => p.connected);
  if (!world.complete && connected.length > 0 && connected.every(p => p.entered)) {
    world.complete = true;
    world.completedAt = world.elapsed;
    emit(world, 'complete');
  }
}

function updateSwitches(world: WorldState, level: LevelDef, bodies: Body[], solids: Solid[], dt: number): void {
  assignSwitchRequirements(world, level);
  const children = new Map<string, Body[]>();
  for (const body of bodies) {
    if (!body.state.grounded) continue;
    for (const support of supportsFor(body, bodies, solids)) {
      const supported = children.get(support.key) ?? [];
      supported.push(body);
      children.set(support.key, supported);
    }
  }
  for (const def of level.switches) {
    const state = world.switches.find(s => s.id === def.id);
    if (!state) continue;
    const supported = new Set<string>();
    let count = 0;
    const visit = (body: Body): void => {
      if (supported.has(body.key)) return;
      supported.add(body.key);
      count += body.player ? 1 : Math.max(0, (body.state as CrateState).weight ?? 2);
      for (const child of children.get(body.key) ?? []) visit(child);
    };
    // A body presses a plate only when at least half of it stands on the plate: a friend
    // butted against a one-body plate from the side is not a second column.
    const footing = Math.min(PLAYER_W / 2, def.w / 2);
    for (const body of bodies) {
      if (body.state.grounded && overlapX(body.state, def) >= footing &&
        bottom(body.state) >= def.y - CONTACT && bottom(body.state) <= bottom(def) + CONTACT) visit(body);
    }
    state.count = count;
    const required = state.required!;
    const automatic = required === 0;
    const pressed = required > 0 && count >= required;
    const wasActive = state.active;
    if (def.mode === 'latch') {
      state.latched ||= pressed;
      state.graceLeft = 0;
      // A temporarily unneeded helper job must not earn a permanent latch.
      state.active = state.latched || automatic;
    } else {
      state.graceLeft = pressed || automatic ? Math.max(0, def.grace ?? 0) : Math.max(0, state.graceLeft - dt);
      if (state.graceLeft < EPS) state.graceLeft = 0;
      state.active = automatic || pressed || state.graceLeft > 0;
    }
    if (state.active !== wasActive) emit(world, 'switch', { targetId: def.id, x: def.x + def.w / 2, y: def.y });
  }
}

/** Remove obsolete supports and resolve a gate/bridge appearing around a body immediately. */
function reconcileSolids(world: WorldState, level: LevelDef, oldSolids: Solid[], bodies: Body[]): Solid[] | null {
  const solids = solidsFor(level, world);
  const oldKeys = new Set(oldSolids.map(s => s.key));
  if (solids.some(s => !oldKeys.has(s.key)) && !repairEmbeddings(bodies, solids, 160)) return null;
  refreshGrounding(bodies, solids);
  return solids;
}

export function syncPlayers(world: WorldState, level: LevelDef, players: PlayerInfo[]): void {
  const previous = new Map(world.players.map(p => [p.id, p]));
  const nextIds = new Set(players.map(p => p.id));
  const oldSolids = solidsFor(level, world);
  for (const p of [...world.players].sort((a, b) => compareId(a.id, b.id))) {
    if (p.connected && !nextIds.has(p.id)) emit(world, 'leave', { playerId: p.id });
  }
  const respawned = new Set<string>();
  world.players = players.map((info, i) => {
    const old = previous.get(info.id);
    if (!old) {
      const p = spawnPlayer(level, info, i);
      p.entered = world.complete;
      if (p.connected) {
        respawned.add(p.id);
        emit(world, 'join', { playerId: p.id });
      }
      return p;
    }
    const resumed = info.connected && !old.connected;
    if (old.connected && !info.connected) emit(world, 'leave', { playerId: old.id });
    Object.assign(old, info);
    if (resumed) {
      const entered = old.entered || world.complete;
      const lastInput = old.lastInput;
      // A returning courier keeps its authoritative place while that place is still
      // valid: inside the world, clear of solids, other bodies and active hazards.
      // Otherwise it respawns exactly as before, so a stack or plate setup survives a blip.
      const rect = rectOf(old);
      const others = [...world.players.filter(p => p !== old && p.connected && !p.entered), ...world.crates];
      const keepPlace = !entered && bottom(rect) <= WORLD_H && rect.x >= 0 && right(rect) <= WORLD_W
        && !oldSolids.some(s => overlaps(rect, s.rect))
        && !others.some(o => overlaps(rect, o))
        && !level.hazards.some(h => isHazardActive(h, world) && overlaps(rect, h));
      if (keepPlace) {
        stopPlayer(old);
        old.entered = entered;
        old.lastInput = lastInput;
      } else {
        Object.assign(old, spawnPlayer(level, info, i), { entered, lastInput });
        respawned.add(old.id);
      }
      emit(world, 'join', { playerId: old.id });
    }
    if (!old.connected || old.entered) stopPlayer(old);
    return old;
  });
  world.partySize = Math.max(world.partySize, players.filter(p => p.connected).length);
  const bodies = bodiesFor(world);
  // Rejoining couriers find space near a spawn; existing couriers never get moved to make room.
  for (const body of bodies.filter(b => b.player && respawned.has(b.player.id))) {
    const blockers = bodies.filter(b => b !== body).map(b => ({ key: b.key, id: b.state.id, rect: rectOf(b.state), previous: rectOf(b.state) }));
    if (!repairEmbeddings([body], [...oldSolids, ...blockers])) {
      resetWorld(world, level);
      return;
    }
  }
  refreshGrounding(bodies, oldSolids);
  updateSwitches(world, level, bodies, oldSolids, 0);
  const solids = reconcileSolids(world, level, oldSolids, bodies);
  if (!solids) {
    resetWorld(world, level);
    return;
  }
  updateSwitches(world, level, bodies, solids, 0);
  completeIfReady(world);
}

/** Build a swept one-axis nonpenetration graph, including moving-solid displacement. */
function axisGraph(bodies: Body[], solids: Solid[], axis: 'x' | 'y'): AxisNode[] | null {
  const horizontal = axis === 'x';
  const size = horizontal ? 'w' : 'h';
  const nodes: AxisNode[] = bodies.map(body => ({
    body,
    size: body.state[size],
    min: 0,
    max: horizontal ? WORLD_W - body.state.w : Infinity,
    after: [],
  })).sort((a, b) => a.body.start[axis] + a.size / 2 - b.body.start[axis] - b.size / 2 || compareId(a.body.key, b.body.key));
  for (const node of nodes) {
    const { body } = node;
    for (const solid of solids) {
      const perpendicular = horizontal ? overlapY(body.start, solid.previous) : overlapX(body.state, solid.rect);
      if (perpendicular <= EPS) continue;
      const start = body.start[axis];
      const oldStart = solid.previous[axis];
      const oldEnd = oldStart + solid.previous[size];
      if (start + node.size <= oldStart + CONTACT) node.max = Math.min(node.max, solid.rect[axis] - node.size);
      else if (start >= oldEnd - CONTACT) node.min = Math.max(node.min, solid.rect[axis] + solid.rect[size]);
      else {
        // Only reachable for authored overlaps or a newly entered perpendicular span.
        const before = solid.rect[axis] - node.size;
        const after = solid.rect[axis] + solid.rect[size];
        if (Math.abs(start - before) <= Math.abs(start - after)) node.max = Math.min(node.max, before);
        else node.min = Math.max(node.min, after);
      }
    }
  }
  for (let i = 0; i < nodes.length; i++) for (let j = i + 1; j < nodes.length; j++) {
    const a = nodes[i];
    const b = nodes[j];
    const perpendicular = horizontal ? overlapY(a.body.start, b.body.start) : overlapX(a.body.state, b.body.state);
    if (perpendicular > EPS) a.after.push(b);
  }
  // Propagate wall/ceiling constraints through a whole crowd, rather than resolving pairs in array order.
  for (const a of nodes) for (const b of a.after) b.min = Math.max(b.min, a.min + a.size);
  for (let i = nodes.length - 1; i >= 0; i--) {
    const a = nodes[i];
    for (const b of a.after) a.max = Math.min(a.max, b.max - a.size);
  }
  return nodes.some(n => n.min > n.max + EPS) ? null : nodes;
}

function supportMotion(body: Body, axis: 'x' | 'y'): number {
  if (!body.support || body.jumped) return 0;
  const { support } = body;
  if (support.body) {
    if (axis === 'y' && overlapX(body.state, support.body.state) <= EPS) return 0;
    return support.body.state[axis] - support.body.start[axis];
  }
  if (support.solid) {
    if (axis === 'y' && overlapX(body.state, support.solid.rect) <= EPS) return 0;
    return support.solid.rect[axis] - support.solid.previous[axis];
  }
  return 0;
}

function solveHorizontal(nodes: AxisNode[]): void {
  const supportOrder = [...nodes].sort((a, b) => b.body.start.y - a.body.start.y || compareId(a.body.key, b.body.key));
  // A support may itself be pushed or stopped. Feed its *resolved* motion up the stack.
  for (let carryPass = 0; carryPass < Math.max(10, nodes.length + 2); carryPass++) {
    const before = nodes.map(n => n.body.state.x);
    const resolvedMotion = new Map(nodes.map(n => [n.body.key, n.body.state.x - n.body.start.x]));
    for (const node of supportOrder) {
      const body = node.body;
      const carry = !body.jumped && body.support?.body
        ? resolvedMotion.get(body.support.body.key) ?? 0
        : supportMotion(body, 'x');
      body.state.x = clamp(body.start.x + body.ownX + carry, node.min, node.max);
    }
    for (let pass = 0; pass < 64; pass++) {
      let largest = 0;
      for (const a of nodes) for (const b of a.after) {
        const depth = a.body.state.x + a.size - b.body.state.x;
        if (depth <= EPS) continue;
        largest = Math.max(largest, depth);
        // Couriers push crates at walking speed; walls still push the entire chain back.
        const shareA = a.body.player && !b.body.player ? 0 : !a.body.player && b.body.player ? 1 : 0.5;
        const roomA = a.body.anchored ? 0 : Math.max(0, a.body.state.x - a.min);
        const roomB = b.body.anchored ? 0 : Math.max(0, b.max - b.body.state.x);
        let moveA = Math.min(roomA, depth * shareA);
        const moveB = Math.min(roomB, depth - moveA);
        moveA += Math.min(roomA - moveA, depth - moveA - moveB);
        a.body.state.x -= moveA;
        b.body.state.x += moveB;
      }
      if (largest <= EPS) break;
    }
    // Exact final projection of the acyclic graph also handles long crate trains without residual overlap.
    for (let i = nodes.length - 1; i >= 0; i--) {
      const a = nodes[i];
      for (const b of a.after) a.body.state.x = Math.min(a.body.state.x, b.body.state.x - a.size);
    }
    if (nodes.every((n, i) => Math.abs(n.body.state.x - before[i]) <= EPS)) break;
  }
  for (const node of nodes) node.body.state.vx = (node.body.state.x - node.body.start.x) / STEP;
}

function solveVertical(nodes: AxisNode[]): void {
  // Lower bodies keep their intended motion; riders settle on them, or follow them upward.
  // Propagated ceiling bounds prevent a jumping base from crushing a rider into a ceiling.
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    const body = node.body;
    body.targetY = body.start.y + body.ownY + supportMotion(body, 'y');
    body.state.y = clamp(body.targetY, node.min, node.max);
    for (const below of node.after) body.state.y = Math.min(body.state.y, below.body.state.y - node.size);
    if (body.state.y > body.targetY + EPS && body.state.vy < 0) body.state.vy = 0;
  }
}

function jump(world: WorldState, body: Body, prediction = false): void {
  const p = body.player!;
  p.vy = -PHYSICS.jump;
  p.grounded = false;
  p.supportId = null;
  p.coyote = 0;
  p.jumpBuffer = 0;
  body.support = null;
  body.jumped = true;
  if (!prediction) emit(world, 'jump', { playerId: p.id, x: p.x + p.w / 2, y: bottom(p) });
}

function acknowledge(world: WorldState, inputs: Record<string, InputState>): Map<string, InputState> {
  const accepted = new Map<string, InputState>();
  for (const p of world.players) {
    if (!p.connected) continue;
    const input = inputs[p.id];
    if (input && Number.isSafeInteger(input.seq) && input.seq >= p.lastInput && input.seq >= 0 &&
      typeof input.left === 'boolean' && typeof input.right === 'boolean' && typeof input.jump === 'boolean') {
      p.lastInput = input.seq;
      accepted.set(p.id, input);
    }
  }
  return accepted;
}

/** Follow the actual X-then-Y path; a thin key, beam or exit cannot be skipped by a fast carrier. */
function crossed(body: Body, target: Rect): boolean {
  const s = body.start;
  const p = body.state;
  const horizontal: Rect = { x: Math.min(s.x, p.x), y: s.y, w: s.w + Math.abs(p.x - s.x), h: s.h };
  const vertical: Rect = { x: p.x, y: Math.min(s.y, p.y), w: p.w, h: p.h + Math.abs(p.y - s.y) };
  return overlaps(horizontal, target) || overlaps(vertical, target);
}

function dangerous(world: WorldState, level: LevelDef, bodies: Body[], swept: boolean): boolean {
  return bodies.some(b => b.state.y > WORLD_H + 64 || (b.player && level.hazards.some(h =>
    isHazardActive(h, world) && (swept ? crossed(b, h) : overlaps(b.state, h)))));
}

function tickWorld(world: WorldState, level: LevelDef, inputs: Record<string, InputState>, prediction = false): boolean {
  const previousSolids = solidsFor(level, world);
  world.tick++;
  // Quantize the clock too, so ten-minute runs do not drift across switch/hazard cycle boundaries.
  world.elapsed = Math.round(world.elapsed * TICK_RATE + 1) / TICK_RATE;
  const accepted = acknowledge(world, inputs);
  if (world.complete) {
    if (!prediction) assignSwitchRequirements(world, level);
    return true;
  }
  const bodies = bodiesFor(world);
  let solids = solidsFor(level, world, previousSolids);
  const movedSolids = new Map(solids.map(s => [s.key, s]));
  for (const body of bodies) {
    const oldSupport = supportsFor(body, bodies, previousSolids)[0] ?? null;
    body.state.grounded = !!oldSupport && (body.state.grounded || body.state.vy >= -EPS);
    body.support = body.state.grounded ? oldSupport : null;
    if (body.support?.solid) body.support.solid = movedSolids.get(body.support.key);
    if (body.player) {
      const p = body.player;
      const input = accepted.get(p.id) ?? EMPTY_INPUT;
      p.coyote = p.grounded ? PHYSICS.coyote : Math.max(0, p.coyote - STEP);
      p.jumpBuffer = Math.max(0, p.jumpBuffer - STEP);
      if (input.jump && !p.jumpHeld) p.jumpBuffer = PHYSICS.buffer;
      p.jumpHeld = input.jump;
      const direction = Number(input.right) - Number(input.left);
      body.ownX = direction * PHYSICS.speed * STEP;
      if (direction) p.facing = direction;
      if (p.jumpBuffer > 0 && (p.grounded || p.coyote > EPS)) jump(world, body, prediction);
      // A holder who stands still on a plate keeps their footing: friends walking into
      // them stop instead of shoving them off the job.
      body.anchored = !body.jumped && direction === 0 && body.state.grounded && !!body.support && !body.support.body
        && level.switches.some(sw => overlapX(body.state, sw) > EPS && Math.abs(bottom(body.state) - bottom(sw)) < 1);
    }
    if (body.state.grounded) body.state.vy = 0;
    body.state.vy = Math.min(PHYSICS.maxFall, body.state.vy + PHYSICS.gravity * STEP);
    body.ownY = body.state.vy * STEP;
  }
  const horizontal = axisGraph(bodies, solids, 'x');
  if (!horizontal) { if (!prediction) resetWorld(world, level); return false; }
  solveHorizontal(horizontal);
  const vertical = axisGraph(bodies, solids, 'y');
  if (!vertical) { if (!prediction) resetWorld(world, level); return false; }
  solveVertical(vertical);
  for (const body of bodies) {
    const support = supportsFor(body, bodies, solids)[0] ?? null;
    body.state.grounded = !!support && (body.state.vy >= -EPS || body.targetY > body.state.y + EPS);
    if (body.state.grounded) body.state.vy = 0;
    if (body.player) {
      const p = body.player;
      p.supportId = p.grounded ? support?.id ?? null : null;
      if (p.grounded) {
        p.coyote = PHYSICS.coyote;
        if (!body.wasGrounded && !prediction) emit(world, 'land', { playerId: p.id, x: p.x + p.w / 2, y: bottom(p) });
        if (p.jumpBuffer > EPS) jump(world, body, prediction);
      }
    }
  }
  if (prediction) {
    // Keep physical contacts coherent, but never materialize speculative gates,
    // remove an exiting body, collect a key, or reset positions after a hazard.
    refreshGrounding(bodies, solids);
    return true;
  }
  updateSwitches(world, level, bodies, solids, STEP);
  const reconciled = reconcileSolids(world, level, solids, bodies);
  if (!reconciled) { resetWorld(world, level); return false; }
  solids = reconciled;
  if (dangerous(world, level, bodies, true)) { resetWorld(world, level); return false; }

  const collected = new Set(world.collectedKeys);
  for (const key of [...level.keys].sort((a, b) => compareId(a.id, b.id))) {
    if (collected.has(key.id)) continue;
    const collector = bodies.find(b => b.player && crossed(b, key));
    if (collector) {
      collected.add(key.id);
      world.collectedKeys.push(key.id);
      emit(world, 'key', { playerId: collector.state.id, targetId: key.id, x: key.x + key.w / 2, y: key.y + key.h / 2 });
    }
  }
  if (level.keys.every(k => collected.has(k.id))) {
    for (const body of bodies) {
      if (body.player && crossed(body, level.exit)) {
        body.player.entered = true;
        stopPlayer(body.player);
        emit(world, 'enter', { playerId: body.player.id, x: level.exit.x + level.exit.w / 2, y: bottom(level.exit) });
      }
    }
  }
  const remaining = bodies.filter(b => !b.player?.entered);
  // Exiting players cease to be collision/support/plate participants in this very tick.
  refreshGrounding(remaining, solids);
  updateSwitches(world, level, remaining, solids, 0);
  const finalSolids = reconcileSolids(world, level, solids, remaining);
  if (!finalSolids || dangerous(world, level, remaining, false)) { resetWorld(world, level); return false; }
  completeIfReady(world);
  return true;
}

/**
 * Authoritative fixed 60 Hz simulation. dt may request a batch of fixed ticks (maximum 15);
 * positive sub-tick deltas request one tick. There is no hidden accumulator or unserialized state.
 */
export function stepWorld(world: WorldState, level: LevelDef, inputs: Record<string, InputState>, dt = STEP): void {
  if (!Number.isFinite(dt) || dt <= 0) return;
  const ticks = Math.max(1, Math.min(15, Math.round(dt * TICK_RATE)));
  const epoch = world.epoch;
  for (let i = 0; i < ticks; i++) {
    tickWorld(world, level, inputs);
    if (world.epoch !== epoch) break; // Never apply an old epoch's held input to the freshly reset party.
  }
}

/**
 * Pure browser movement replay from an authoritative snapshot. Commands are
 * chronological held-input durations in seconds, capped at 0.25 seconds/15 ticks.
 * Sample each tick's ending input (including a final partial tick) so a fresh
 * press is visible immediately without rounding every short segment up a tick.
 * Only body motion and the prediction clock advance; semantics and returned
 * acknowledgements stay authoritative. No state survives outside the result.
 */
export function predictMovementWorld(world: WorldState, level: LevelDef, localId: string,
  commands: { input: InputState; duration: number }[]): WorldState {
  let predicted = structuredClone(world);
  const local = world.players.find(p => p.id === localId);
  if (world.complete || !local?.connected || local.entered) return predicted;

  const timeline: { input: InputState; until: number }[] = [];
  let duration = 0;
  for (const command of commands) {
    if (!Number.isFinite(command.duration) || command.duration <= 0) continue;
    duration = Math.min(15 * STEP, duration + command.duration);
    timeline.push({ input: command.input, until: duration });
    if (duration >= 15 * STEP) break;
  }
  if (!timeline.length) return predicted;

  const bodies = bodiesFor(world);
  const previousTime = Math.max(0, Math.round(world.elapsed * TICK_RATE - 1) / TICK_RATE);
  const solids = solidsFor(level, world, solidsFor(level, { ...world, elapsed: previousTime }));
  const remoteInputs: Record<string, InputState> = {};
  for (const body of bodies) {
    const p = body.player;
    if (!p || p.id === localId) continue;
    const support = p.grounded ? supportsFor(body, bodies, solids)[0] : undefined;
    const supportVx = support?.body ? support.body.state.vx
      : support?.solid ? (support.solid.rect.x - support.solid.previous.x) / STEP : 0;
    // Infer held intent ONCE from the received snapshot. Carried riders must not
    // turn their support's velocity into another walk, or re-infer after a push.
    const ownVx = p.vx - supportVx;
    remoteInputs[p.id] = { left: ownVx < -EPS, right: ownVx > EPS, jump: p.jumpHeld, seq: p.lastInput };
  }

  const ticks = Math.min(15, Math.max(1, Math.ceil(duration * TICK_RATE - EPS)));
  let commandIndex = 0;
  for (let tick = 0; tick < ticks; tick++) {
    const at = Math.min((tick + 1) * STEP, duration);
    while (commandIndex + 1 < timeline.length && timeline[commandIndex].until < at - EPS) commandIndex++;
    // Only mutable bodies need a per-tick checkpoint. The initial deep clone
    // owns the frozen semantic arrays, which movement-only ticks never write.
    const next = {
      ...predicted,
      players: predicted.players.map(p => ({ ...p })),
      crates: predicted.crates.map(c => ({ ...c })),
    };
    if (!tickWorld(next, level, { ...remoteInputs, [localId]: timeline[commandIndex].input }, true)) break;
    predicted = next; // An impossible crush discards the candidate, including its clock.
  }
  const acknowledgements = new Map(world.players.map(p => [p.id, p.lastInput]));
  for (const p of predicted.players) p.lastInput = acknowledgements.get(p.id)!;
  return predicted;
}
